export const WINDOW_SCROLL_RESTORE_MAX_MS = 1500
export const WINDOW_SCROLL_RESTORE_MIN_MS = 120
export const WINDOW_SCROLL_RESTORE_STABLE_FRAMES = 3
export const TOUCH_SCROLL_INTENT_PX = 12

const EPSILON_PX = 2
const SCROLL_STORAGE_PREFIX = 'npf:window-scroll:'

export const touchClientY = event => {
  const touch =
    (event && event.touches && event.touches[0]) ||
    (event && event.changedTouches && event.changedTouches[0])
  if (!touch) {
    return null
  }
  const y = touch.clientY
  return typeof y === 'number' && !Number.isNaN(y) ? y : null
}

const USER_SCROLL_KEYS = {
  ArrowUp: true,
  ArrowDown: true,
  PageUp: true,
  PageDown: true,
  Home: true,
  End: true,
  ' ': true,
  Spacebar: true
}

let restoreGeneration = 0
let stopCurrentRestore = null

const withTrailingSlash = pathname => {
  if (!pathname || pathname === '/') {
    return pathname
  }
  return pathname.endsWith('/') ? pathname : `${pathname}/`
}

export const isAdvanceArticlePath = pathname => {
  const path = withTrailingSlash(pathname || '')
  return /^\/advance\/[^/]+\/$/.test(path)
}

const storageKeyForPath = pathname =>
  `${SCROLL_STORAGE_PREFIX}${withTrailingSlash(pathname || '')}`

export const persistWindowScroll = (pathname, y) => {
  if (typeof window === 'undefined' || !window.sessionStorage || !pathname) {
    return
  }
  if (typeof y !== 'number' || Number.isNaN(y)) {
    return
  }
  const value = String(Math.max(0, y))
  try {
    const path = withTrailingSlash(pathname)
    window.sessionStorage.setItem(storageKeyForPath(path), value)
    if (path && path !== '/') {
      const alt = path.endsWith('/') ? path.slice(0, -1) : `${path}/`
      window.sessionStorage.setItem(storageKeyForPath(alt), value)
    }
  }
  catch (_error) {
    return
  }
}

export const readPersistedWindowScroll = pathname => {
  if (typeof window === 'undefined' || !window.sessionStorage || !pathname) {
    return null
  }
  const path = withTrailingSlash(pathname)
  const keys = [storageKeyForPath(path)]
  if (path && path !== '/') {
    const alt = path.endsWith('/') ? path.slice(0, -1) : `${path}/`
    keys.push(storageKeyForPath(alt))
  }
  try {
    for (let i = 0; i < keys.length; i += 1) {
      const raw = window.sessionStorage.getItem(keys[i])
      if (raw == null || raw === '') {
        continue
      }
      const y = Number(raw)
      if (!Number.isNaN(y)) {
        return y
      }
    }
  }
  catch (_error) {
    return null
  }
  return null
}

export const setManualScrollRestoration = () => {
  if (typeof window === 'undefined' || !window.history) {
    return
  }
  if ('scrollRestoration' in window.history) {
    window.history.scrollRestoration = 'manual'
  }
}

export const syncScrollRestorationForPath = pathname => {
  if (typeof window === 'undefined') {
    return
  }
  const isArticle = isAdvanceArticlePath(pathname)
  if (window.history && 'scrollRestoration' in window.history) {
    window.history.scrollRestoration = isArticle ? 'manual' : 'auto'
  }
  // Ads/images inserting above the viewport would otherwise shift Y ~90px
  // after restore ends (desktop "a tiny bit lower than originally").
  if (typeof document !== 'undefined' && document.documentElement) {
    const style = document.documentElement.style
    if (style) {
      style.overflowAnchor = isArticle ? 'none' : ''
    }
  }
}

export const isWindowScrollRestoreActive = () =>
  typeof stopCurrentRestore === 'function'

const getScrollY = () => {
  if (typeof window === 'undefined') {
    return 0
  }
  return window.pageYOffset || window.scrollY || 0
}

const getMaxScrollY = () => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 0
  }
  const root = document.documentElement
  const height =
    root && typeof root.scrollHeight === 'number' ? root.scrollHeight : 0
  return Math.max(0, height - (window.innerHeight || 0))
}

export const cancelWindowScrollRestore = () => {
  restoreGeneration += 1
  if (typeof stopCurrentRestore === 'function') {
    const stop = stopCurrentRestore
    stopCurrentRestore = null
    stop()
  }
}

// Keep applying a saved window Y until the document is tall enough to hold it
// (lazy images, GPT slots, related-article rail). Run until the time cap so a
// later native/focus scroll cannot undo the restore. Stop early if the user
// scrolls. `stickToTarget` pulls back from overflow-anchor / max-scroll pinning
// (refresh and article Back/Forward). Tiny touchmoves from a tap do not count
// as user intent.
export const restoreWindowScroll = (top, options = {}) => {
  if (typeof window === 'undefined') {
    return
  }

  const stickToTarget = Boolean(options && options.stickToTarget)
  const y =
    typeof top === 'number' && !Number.isNaN(top) ? Math.max(top, 0) : 0

  window.__NPF_LAST_SCROLL_RESTORE_Y = y

  cancelWindowScrollRestore()
  const generation = restoreGeneration
  const startedAt = Date.now()

  let rafId = 0
  let zeroTimeoutId = 0
  let maxTimeoutId = 0
  let armTimeoutId = 0
  let observer = null
  let applying = false
  let userIntentArmed = false
  let touchStartY = null

  const stillActive = () => generation === restoreGeneration

  const apply = () => {
    if (!stillActive()) {
      return
    }
    const current = getScrollY()
    const maxScroll = getMaxScrollY()
    const desired = Math.min(y, maxScroll)

    // Growth above the viewport (overflow-anchor) — do not pull back unless
    // stickToTarget (refresh and article Back/Forward) or the viewport is
    // pinned to the document bottom.
    if (
      !stickToTarget &&
      current > y + EPSILON_PX &&
      current < maxScroll - EPSILON_PX
    ) {
      return
    }

    if (Math.abs(current - desired) <= EPSILON_PX) {
      return
    }

    applying = true
    window.scrollTo(0, desired)
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.scrollTop = desired
    }
    window.requestAnimationFrame(() => {
      applying = false
    })
  }

  const cleanup = () => {
    if (rafId) {
      window.cancelAnimationFrame(rafId)
      rafId = 0
    }
    if (zeroTimeoutId) {
      window.clearTimeout(zeroTimeoutId)
      zeroTimeoutId = 0
    }
    if (maxTimeoutId) {
      window.clearTimeout(maxTimeoutId)
      maxTimeoutId = 0
    }
    if (armTimeoutId) {
      window.clearTimeout(armTimeoutId)
      armTimeoutId = 0
    }
    if (observer) {
      observer.disconnect()
      observer = null
    }
    window.removeEventListener('wheel', onUserIntent)
    window.removeEventListener('touchstart', onTouchStart)
    window.removeEventListener('touchmove', onTouchMove)
    window.removeEventListener('keydown', onKeyDown)
    window.removeEventListener('scroll', onScroll)
    if (stopCurrentRestore === cleanup) {
      stopCurrentRestore = null
    }
  }

  const stopIfActive = () => {
    if (!stillActive()) {
      return
    }
    restoreGeneration += 1
    cleanup()
  }

  const onUserIntent = () => {
    if (!userIntentArmed) {
      return
    }
    stopIfActive()
  }

  const onTouchStart = event => {
    touchStartY = touchClientY(event)
  }

  const onTouchMove = event => {
    if (!userIntentArmed) {
      return
    }
    const y = touchClientY(event)
    if (touchStartY == null || y == null) {
      return
    }
    if (Math.abs(y - touchStartY) < TOUCH_SCROLL_INTENT_PX) {
      return
    }
    stopIfActive()
  }

  const onKeyDown = event => {
    if (!userIntentArmed || !event || !USER_SCROLL_KEYS[event.key]) {
      return
    }
    stopIfActive()
  }

  const onScroll = () => {
    if (!stillActive() || applying) {
      return
    }
    if (stickToTarget || getScrollY() < y - EPSILON_PX) {
      apply()
    }
  }

  const tick = () => {
    if (!stillActive()) {
      return
    }

    apply()

    if (Date.now() - startedAt >= WINDOW_SCROLL_RESTORE_MAX_MS) {
      apply()
      cleanup()
      return
    }

    rafId = window.requestAnimationFrame(tick)
  }

  stopCurrentRestore = cleanup
  apply()
  zeroTimeoutId = window.setTimeout(apply, 0)

  if (typeof ResizeObserver === 'function') {
    observer = new ResizeObserver(() => {
      apply()
    })
    const contentRegion =
      typeof document !== 'undefined'
        ? document.getElementById('ContentRegion')
        : null
    if (contentRegion) {
      observer.observe(contentRegion)
    }
    if (typeof document !== 'undefined' && document.documentElement) {
      observer.observe(document.documentElement)
    }
  }

  window.addEventListener('wheel', onUserIntent, { passive: true })
  window.addEventListener('touchstart', onTouchStart, { passive: true })
  window.addEventListener('touchmove', onTouchMove, { passive: true })
  window.addEventListener('keydown', onKeyDown)
  window.addEventListener('scroll', onScroll, { passive: true })

  armTimeoutId = window.setTimeout(() => {
    userIntentArmed = true
  }, stickToTarget ? 300 : WINDOW_SCROLL_RESTORE_MIN_MS)

  maxTimeoutId = window.setTimeout(() => {
    if (!stillActive()) {
      return
    }
    apply()
    cleanup()
  }, WINDOW_SCROLL_RESTORE_MAX_MS)

  rafId = window.requestAnimationFrame(tick)
}
